import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { FinturfLiveGateCard } from '../components/FinturfLiveGateCard';
import { getApiErrorMessage } from '../lib/errorUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Landmark,
  FileText,
  DollarSign,
  TrendingUp,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
  Building2,
  Percent,
  Calendar,
  ArrowRight,
  Settings,
  Loader2,
  Eye,
  Pencil,
  Trash2,
  Power,
  PowerOff,
  Shield,
  KeyRound,
  Lock,
  Unlock,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatET } from '../utils/timeUtils';

// Status badges
const statusConfig = {
  submitted: { color: 'bg-blue-100 text-blue-800', label: 'Submitted' },
  processing: { color: 'bg-yellow-100 text-yellow-800', label: 'Processing' },
  offers_received: { color: 'bg-purple-100 text-purple-800', label: 'Offers Ready' },
  offer_selected: { color: 'bg-indigo-100 text-indigo-800', label: 'Offer Selected' },
  funding_pending: { color: 'bg-orange-100 text-orange-800', label: 'Funding Pending' },
  funded: { color: 'bg-green-100 text-green-800', label: 'Funded' },
  rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
  cancelled: { color: 'bg-slate-100 text-slate-800', label: 'Cancelled' },
};

const offerStatusConfig = {
  pending: { color: 'bg-blue-100 text-blue-800', label: 'Pending' },
  selected: { color: 'bg-green-100 text-green-800', label: 'Selected' },
  declined: { color: 'bg-slate-100 text-slate-800', label: 'Declined' },
  expired: { color: 'bg-red-100 text-red-800', label: 'Expired' },
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, subtitle, trend, color = 'text-accent' }) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full bg-accent/10 ${color}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
      {trend !== undefined && (
        <div className="mt-2 flex items-center text-sm">
          {trend >= 0 ? (
            <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
          ) : (
            <TrendingUp className="h-4 w-4 text-red-500 mr-1 rotate-180" />
          )}
          <span className={trend >= 0 ? 'text-green-600' : 'text-red-600'}>
            {Math.abs(trend)}%
          </span>
          <span className="text-slate-400 ml-1">vs last month</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Empty state for new lender form
const emptyLender = {
  name: '',
  api_endpoint: '',
  api_key: '',
  api_type: 'mock',
  status: 'active',
  priority: 100,
  min_loan_amount: 1000,
  max_loan_amount: 100000,
  min_apr: 5.0,
  max_apr: 36.0,
  enabled: true,
  enabled_companies: [],
  description: '',
};

// Empty commission rule
const emptyCommissionRule = {
  lender_id: '',
  commission_type: 'percentage',
  percentage: 2.0,
  flat_fee: null,
  tier_min: null,
  tier_max: null,
  active: true,
};

const LendingPage = () => {
  const { user, isAdmin, isInternal, canAccessLending } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Applications
  const [applications, setApplications] = useState([]);
  
  // Offers
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  
  // Lenders (admin)
  const [lenders, setLenders] = useState([]);
  const [isLenderDialogOpen, setIsLenderDialogOpen] = useState(false);
  const [editingLender, setEditingLender] = useState(null);
  const [lenderForm, setLenderForm] = useState(emptyLender);
  const [savingLender, setSavingLender] = useState(false);
  const [deleteLenderId, setDeleteLenderId] = useState(null);
  
  // Commission Rules (admin)
  const [commissionRules, setCommissionRules] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [isCommissionRuleDialogOpen, setIsCommissionRuleDialogOpen] = useState(false);
  const [commissionRuleForm, setCommissionRuleForm] = useState(emptyCommissionRule);
  const [savingCommissionRule, setSavingCommissionRule] = useState(false);
  
  // Companies for assignment
  const [companies, setCompanies] = useState([]);
  
  // Application delete confirmation
  const [deleteApplicationId, setDeleteApplicationId] = useState(null);

  // v3.14-167: Centralized Lending Submission State
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchingClients, setSearchingClients] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [submittingToLender, setSubmittingToLender] = useState(false);
  const [lenderOffers, setLenderOffers] = useState([]);
  const [showOffersModal, setShowOffersModal] = useState(false);
  const [lenderAppId, setLenderAppId] = useState(null);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [savingSelectedOffer, setSavingSelectedOffer] = useState(false);

  // Fetch lending stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/lending/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch lending stats:', error);
    }
  }, []);

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    try {
      const res = await api.get('/lending/applications');
      setApplications(res.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    }
  }, []);

  // Fetch offers for an application
  const fetchOffers = useCallback(async (applicationId) => {
    setLoadingOffers(true);
    try {
      const res = await api.get(`/lending/offers?application_id=${applicationId}`);
      setOffers(res.data);
    } catch (error) {
      console.error('Failed to fetch offers:', error);
    } finally {
      setLoadingOffers(false);
    }
  }, []);

  // Fetch lenders (admin)
  const fetchLenders = useCallback(async () => {
    try {
      const res = await api.get('/lending/lenders?include_inactive=true');
      setLenders(res.data);
    } catch (error) {
      console.error('Failed to fetch lenders:', error);
    }
  }, []);

  // Fetch commissions (admin)
  const fetchCommissions = useCallback(async () => {
    try {
      const res = await api.get('/lending/commissions');
      setCommissions(res.data);
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    }
  }, []);

  // Fetch commission rules (admin)
  const fetchCommissionRules = useCallback(async () => {
    try {
      const res = await api.get('/lending/commission-rules');
      setCommissionRules(res.data);
    } catch (error) {
      console.error('Failed to fetch commission rules:', error);
    }
  }, []);

  // Fetch companies
  const fetchCompanies = useCallback(async () => {
    try {
      const res = await api.get('/companies/external');
      setCompanies(res.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchApplications(),
        isAdmin() && fetchLenders(),
        isInternal() && fetchCommissions(),
        isInternal() && fetchCommissionRules(),
        isAdmin() && fetchCompanies(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchApplications, fetchLenders, fetchCommissions, fetchCommissionRules, fetchCompanies, isAdmin, isInternal]);

  useEffect(() => {
    if (selectedApplication) {
      fetchOffers(selectedApplication.id);
    }
  }, [selectedApplication, fetchOffers]);

  // Select an offer
  const handleSelectOffer = async (offerId) => {
    try {
      await api.post(`/lending/offers/${offerId}/select`);
      toast.success('Offer selected successfully');
      fetchOffers(selectedApplication.id);
      fetchApplications();
      fetchStats();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to select offer'));
    }
  };

  // Mark as funded (admin)
  const handleMarkFunded = async (offerId, amount) => {
    try {
      const res = await api.post(`/lending/offers/${offerId}/fund?funded_amount=${amount}`);
      toast.success(`Deal funded! Commission: $${res.data.commission_amount}`);
      fetchOffers(selectedApplication.id);
      fetchApplications();
      fetchStats();
      fetchCommissions();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to mark as funded'));
    }
  };

  // Open lender dialog for create
  const handleOpenCreateLender = () => {
    setEditingLender(null);
    setLenderForm(emptyLender);
    setIsLenderDialogOpen(true);
  };

  // Open lender dialog for edit
  const handleOpenEditLender = (lender) => {
    setEditingLender(lender);
    setLenderForm({
      name: lender.name || '',
      api_endpoint: lender.api_endpoint || '',
      api_key: '', // Never pre-fill API key for security
      api_type: lender.api_type || 'mock',
      status: lender.status || 'active',
      priority: lender.priority || 100,
      min_loan_amount: lender.min_loan_amount || 1000,
      max_loan_amount: lender.max_loan_amount || 100000,
      min_apr: lender.min_apr || 5.0,
      max_apr: lender.max_apr || 36.0,
      enabled: lender.enabled !== false,
      enabled_companies: lender.enabled_companies || [],
      description: lender.description || '',
    });
    setIsLenderDialogOpen(true);
  };

  // Save lender (create or update)
  const handleSaveLender = async () => {
    if (!lenderForm.name.trim()) {
      toast.error('Lender name is required');
      return;
    }

    setSavingLender(true);
    try {
      const payload = {
        ...lenderForm,
        priority: parseInt(lenderForm.priority) || 100,
        min_loan_amount: parseFloat(lenderForm.min_loan_amount) || 1000,
        max_loan_amount: parseFloat(lenderForm.max_loan_amount) || 100000,
        min_apr: parseFloat(lenderForm.min_apr) || 5.0,
        max_apr: parseFloat(lenderForm.max_apr) || 36.0,
      };

      // Only include api_key if it's been changed (not empty)
      if (!payload.api_key) {
        delete payload.api_key;
      }

      if (editingLender) {
        await api.put(`/lending/lenders/${editingLender.id}`, payload);
        toast.success('Lender updated successfully');
      } else {
        await api.post('/lending/lenders', payload);
        toast.success('Lender created successfully');
      }

      setIsLenderDialogOpen(false);
      setEditingLender(null);
      setLenderForm(emptyLender);
      fetchLenders();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save lender'));
    } finally {
      setSavingLender(false);
    }
  };

  // Toggle lender enabled status
  const handleToggleLenderEnabled = async (lender) => {
    try {
      await api.put(`/lending/lenders/${lender.id}`, {
        enabled: !lender.enabled,
      });
      toast.success(`Lender ${lender.enabled ? 'disabled' : 'enabled'}`);
      fetchLenders();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update lender'));
    }
  };

  // Delete lender
  const handleDeleteLender = async () => {
    if (!deleteLenderId) return;
    
    try {
      await api.delete(`/lending/lenders/${deleteLenderId}`);
      toast.success('Lender deleted successfully');
      setDeleteLenderId(null);
      fetchLenders();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete lender'));
    }
  };

  // Open commission rule dialog
  const handleOpenCreateCommissionRule = () => {
    setCommissionRuleForm(emptyCommissionRule);
    setIsCommissionRuleDialogOpen(true);
  };

  // Save commission rule
  const handleSaveCommissionRule = async () => {
    if (!commissionRuleForm.lender_id) {
      toast.error('Please select a lender');
      return;
    }

    setSavingCommissionRule(true);
    try {
      const payload = {
        ...commissionRuleForm,
        percentage: commissionRuleForm.commission_type === 'percentage' 
          ? parseFloat(commissionRuleForm.percentage) || 2.0 
          : null,
        flat_fee: commissionRuleForm.commission_type === 'flat_fee'
          ? parseFloat(commissionRuleForm.flat_fee) || 0
          : null,
        tier_min: commissionRuleForm.tier_min ? parseFloat(commissionRuleForm.tier_min) : null,
        tier_max: commissionRuleForm.tier_max ? parseFloat(commissionRuleForm.tier_max) : null,
      };

      await api.post('/lending/commission-rules', payload);
      toast.success('Commission rule created');
      setIsCommissionRuleDialogOpen(false);
      setCommissionRuleForm(emptyCommissionRule);
      fetchCommissionRules();
      fetchLenders(); // Refresh to show updated commission info
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create commission rule'));
    } finally {
      setSavingCommissionRule(false);
    }
  };

  // Update commission status
  const handleUpdateCommissionStatus = async (commissionId, status) => {
    try {
      await api.put(`/lending/commissions/${commissionId}/status?status=${status}`);
      toast.success(`Commission ${status}`);
      fetchCommissions();
      fetchStats();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update commission'));
    }
  };

  // Delete application (admin only)
  const handleDeleteApplication = async () => {
    if (!deleteApplicationId) return;
    
    try {
      await api.delete(`/lending/applications/${deleteApplicationId}`);
      toast.success('Application deleted successfully');
      setDeleteApplicationId(null);
      setSelectedApplication(null);
      setOffers([]);
      fetchApplications();
      fetchStats();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete application'));
    }
  };

  // v3.14-167: Search clients for lending submission
  const handleClientSearch = async (query) => {
    setClientSearch(query);
    if (!query || query.length < 2) {
      setClientResults([]);
      return;
    }
    setSearchingClients(true);
    try {
      const res = await api.get(`/clients?search=${encodeURIComponent(query)}&limit=10`);
      setClientResults(res.data?.clients || res.data || []);
    } catch (error) {
      console.error('Failed to search clients:', error);
      setClientResults([]);
    } finally {
      setSearchingClients(false);
    }
  };

  // v3.14-167: Send client to lender (reuses existing Finturf API)
  const handleSendToLender = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }
    if (!selectedPlatform) {
      toast.error('Please select a lending platform');
      return;
    }

    setSubmittingToLender(true);
    try {
      // Backend mapping: primary → Finturf, secondary → Flexxbuy (future)
      // Reuse existing Finturf submit API
      const submitBody = {
        client_id: selectedClient.id,
        full_name: selectedClient.full_name || '',
        email: selectedClient.email || '',
        phone: selectedClient.phone || '',
        ssn: selectedClient.ssn || '',
        dob: selectedClient.date_of_birth || '',
        address: selectedClient.street_address || '',
        city: selectedClient.city || '',
        state: selectedClient.state || '',
        zip: selectedClient.zip_code || '',
        income: selectedClient.monthly_income ? parseFloat(selectedClient.monthly_income) : null,
        employment: selectedClient.income_source || selectedClient.employment_type || selectedClient.employment_status || '',
        residence: selectedClient.residence_type || '',
        loan_amount: selectedClient.deal_amount ? parseFloat(selectedClient.deal_amount) : (selectedClient.loan_amount_requested ? parseFloat(selectedClient.loan_amount_requested) : null),
        purpose: selectedClient.loan_purpose || 'Travel',
      };
      // v3.21-1842 · Finturf duplicate guard: a 2nd application needs explicit confirmation.
      let submitRes;
      try {
        submitRes = await api.post('/finturf/submit', submitBody);
      } catch (dupErr) {
        const d = dupErr?.response?.data?.detail;
        if (dupErr?.response?.status !== 409 || !d?.requires_confirmation) throw dupErr;
        if (!window.confirm(`${d.message}\n\nSubmit a NEW Finturf application anyway?`)) { toast.info('Submission cancelled'); return; }
        submitRes = await api.post('/finturf/submit', { ...submitBody, confirm_resubmit: true });
      }

      // v3.21-1672 · Canonical Finturf response wiring.
      const identifier = submitRes.data?.identifier;
      const redirectUrl = submitRes.data?.redirect_url;
      const clientIdAssigned = submitRes.data?.client_id_assigned;
      const appId = submitRes.data?.application_id;
      setLenderAppId(appId);
      // v3.21-1682 · Removed dead references to `setLenderFinturfIdentifier`
      // and `setLenderContinueUrl` — these setters were never declared in
      // this component; `identifier` and `redirectUrl` remain in scope for
      // the toast/offers branches below.

      const offers = submitRes.data?.offers || [];
      const apiError = submitRes.data?.error;
      const idConflict = submitRes.data?.application_id_conflict;
      if (idConflict) {
        toast.warning(`Finturf Application ID ${idConflict.new} was not saved — client already has ${idConflict.existing}. Flagged for review.`);
      }

      if (apiError) {
        toast.error(`PTC Finance API error: ${apiError}`);
      } else if (offers.length > 0) {
        setLenderOffers(offers);
        setShowOffersModal(true);
      } else if (identifier && redirectUrl) {
        // v3.21-1672 · Application created — customer must complete in Finturf.
        toast.success(`PTC Finance application ${identifier} created${clientIdAssigned ? ' (Client ID set)' : ''}. Customer must complete in PTC Finance.`);
      } else if (identifier) {
        toast.success(`PTC Finance application ${identifier} created${clientIdAssigned ? ' (Client ID set)' : ''}.`);
      } else {
        toast.info('No financing offers available at this time');
      }

      // Refresh applications list
      fetchApplications();
      fetchStats();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to submit to lender'));
    } finally {
      setSubmittingToLender(false);
    }
  };

  // v3.14-167: Save ONE selected offer from lending modal
  const handleSaveLenderOffer = async () => {
    if (!selectedOfferId || !lenderAppId || !selectedClient) {
      toast.error('Please select an offer');
      return;
    }

    setSavingSelectedOffer(true);
    try {
      // Reuse existing single-offer select API
      await api.post(`/finturf/select-offer/${lenderAppId}`, {
        offer_id: selectedOfferId,
        client_id: selectedClient.id,
      });

      toast.success('Offer selected and saved to client record');
      setShowOffersModal(false);
      setLenderOffers([]);
      setSelectedOfferId(null);
      setSelectedClient(null);
      setClientSearch('');
      setSelectedPlatform('');
      fetchApplications();
      fetchStats();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save selected offer'));
    } finally {
      setSavingSelectedOffer(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  // Handle request access
  const handleRequestAccess = async () => {
    try {
      await api.post('/lending/request-access');
      toast.success('Access request submitted! A PTC administrator will review your request.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to submit access request'));
    }
  };

  // Access denied screen for partners when lending is not enabled - Upgrade Promotion
  return (
    <div className="space-y-6" data-testid="lending-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary">Lending Marketplace</h1>
          <p className="text-sm text-slate-500 mt-1">
            PTC Client Hub + EasyQuickFund
          </p>
        </div>
      </div>
      {['ptc_admin', 'ptc_director'].includes(user?.role) && <FinturfLiveGateCard role={user?.role} />}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="lending-tab-dashboard">
            <TrendingUp className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="applications" className="flex items-center gap-2" data-testid="lending-tab-applications">
            <FileText className="h-4 w-4" />
            Applications
          </TabsTrigger>
          {isAdmin() && (
            <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="lending-tab-settings">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
          )}
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Applications Submitted"
              value={stats?.applications_submitted || 0}
              icon={FileText}
            />
            <StatCard
              title="Offers Received"
              value={stats?.offers_received || 0}
              icon={Landmark}
              subtitle={`${stats?.offers_selected || 0} selected`}
            />
            <StatCard
              title="Deals Funded"
              value={stats?.deals_funded || 0}
              icon={CheckCircle}
              color="text-green-600"
            />
            <StatCard
              title="Conversion Rate"
              value={stats?.conversion_rate ? `${stats.conversion_rate}%` : 'N/A'}
              icon={Percent}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Total Funded</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-green-600">
                  ${(stats?.total_funded_amount || 0).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Applications */}
          <Card className="mt-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Recent Applications</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveTab('applications')}
              >
                View All <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {applications.length === 0 ? (
                <p className="text-center text-slate-500 py-4">No applications yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      {isAdmin() && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.slice(0, 5).map((app) => {
                      const config = statusConfig[app.application_status] || statusConfig.submitted;
                      return (
                        <TableRow key={app.id}>
                          <TableCell className="font-medium">
                            <Link to={`/clients/${app.client_id}`} className="hover:text-accent">
                              {app.client_name || 'Unknown'}
                            </Link>
                          </TableCell>
                          <TableCell>${app.requested_amount?.toLocaleString()}</TableCell>
                          <TableCell>
                            <Badge className={config.color}>{config.label}</Badge>
                          </TableCell>
                          <TableCell>
                            {app.created_at && formatET(app.created_at, 'MMM d, yyyy')}
                          </TableCell>
                          {isAdmin() && (
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => setDeleteApplicationId(app.id)}
                                data-testid={`delete-application-${app.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Applications Tab */}
        <TabsContent value="applications" className="mt-6">
          {/* v3.14-167: Submit to Lender Section */}
          <Card className="mb-6 border-blue-200 bg-blue-50/30" data-testid="submit-to-lender-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Landmark className="h-5 w-5 text-blue-600" />
                Submit to Lender
              </CardTitle>
              <CardDescription>Select a client and platform, then send for financing offers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
                {/* Client Search */}
                <div className="flex-1 w-full relative">
                  <Label className="text-sm font-medium">Client</Label>
                  {selectedClient ? (
                    <div className="flex items-center gap-2 mt-1 p-2 border rounded-md bg-white" data-testid="selected-client-display">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{selectedClient.full_name}</p>
                        <p className="text-xs text-slate-500">{selectedClient.email} {selectedClient.deal_amount ? `| Deal: $${Number(selectedClient.deal_amount).toLocaleString()}` : ''}</p>
                        <p className="text-[11px] text-slate-500" data-testid="lending-client-identifiers">
                          EQF Application #: <span className="font-mono">{selectedClient.credit_app_confirmation_number || '—'}</span>
                          {' · '}Finturf Application ID: <span className="font-mono">{selectedClient.current_finturf_application_id || selectedClient.deal_number || '—'}</span>
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedClient(null); setClientSearch(''); setClientResults([]); }}
                        data-testid="clear-client-btn"
                      >
                        <XCircle className="h-4 w-4 text-slate-400" />
                      </Button>
                    </div>
                  ) : (
                    <div className="relative mt-1">
                      <Input
                        value={clientSearch}
                        onChange={(e) => handleClientSearch(e.target.value)}
                        placeholder="Search by name or email..."
                        className="bg-white"
                        data-testid="client-search-input"
                      />
                      {searchingClients && (
                        <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      )}
                      {clientResults.length > 0 && !selectedClient && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto" data-testid="client-search-results">
                          {clientResults.map((c) => (
                            <div
                              key={c.id}
                              className="px-3 py-2 hover:bg-slate-50 cursor-pointer border-b last:border-0"
                              onClick={() => {
                                setSelectedClient(c);
                                setClientSearch('');
                                setClientResults([]);
                              }}
                              data-testid={`client-result-${c.id}`}
                            >
                              <p className="text-sm font-medium">{c.full_name}</p>
                              <p className="text-xs text-slate-500">{c.email} {c.company_name ? `| ${c.company_name}` : ''}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Platform Selector */}
                <div className="w-full sm:w-56">
                  <Label className="text-sm font-medium">Lending Platform</Label>
                  <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="platform-select">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="primary">Primary Lending</SelectItem>
                      <SelectItem value="secondary">Secondary Lending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Send Button */}
                <Button
                  className="bg-accent hover:bg-gold-dark h-10 px-6"
                  onClick={handleSendToLender}
                  disabled={submittingToLender || !selectedClient || !selectedPlatform}
                  data-testid="send-to-lender-btn"
                >
                  {submittingToLender ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="h-4 w-4 mr-2" />
                  )}
                  {submittingToLender ? 'Sending...' : 'Start Financing Application'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Applications List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Loan Applications</CardTitle>
              </CardHeader>
              <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                {applications.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No applications</p>
                ) : (
                  applications.map((app) => {
                    const config = statusConfig[app.application_status] || statusConfig.submitted;
                    const isSelected = selectedApplication?.id === app.id;
                    return (
                      <div
                        key={app.id}
                        onClick={() => setSelectedApplication(app)}
                        className={`p-4 border-b cursor-pointer transition-colors ${
                          isSelected ? 'bg-accent/10 border-l-4 border-l-accent' : 'hover:bg-slate-50'
                        }`}
                        data-testid={`application-item-${app.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{app.client_name || 'Unknown'}</p>
                            <p className="text-sm text-slate-500">
                              ${app.requested_amount?.toLocaleString()} {app.loan_purpose ? `- ${app.loan_purpose}` : ''}
                            </p>
                          </div>
                          <Badge className={config.color}>{config.label}</Badge>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          {app.created_at && formatET(app.created_at, 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            {/* Offers Panel */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {selectedApplication ? 'Lender Offers' : 'Select an Application'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!selectedApplication ? (
                  <div className="text-center py-12 text-slate-400">
                    <Landmark className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Select an application to view offers</p>
                  </div>
                ) : loadingOffers ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-accent" />
                  </div>
                ) : offers.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <p>No offers available</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {offers.map((offer) => {
                      const config = offerStatusConfig[offer.status] || offerStatusConfig.pending;
                      return (
                        <div
                          key={offer.id}
                          className={`p-4 border rounded-lg ${
                            offer.status === 'selected' ? 'border-green-500 bg-green-50' : ''
                          }`}
                          data-testid={`offer-item-${offer.id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-5 w-5 text-slate-600" />
                              <span className="font-semibold">{offer.lender_name}</span>
                            </div>
                            <Badge className={config.color}>{config.label}</Badge>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Approved Amount</p>
                              <p className="font-bold text-lg">${offer.approved_amount?.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">APR</p>
                              <p className="font-bold text-lg">{offer.apr}%</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Term</p>
                              <p className="font-medium">{offer.term_months} months</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Monthly Payment</p>
                              <p className="font-medium">${offer.monthly_payment?.toLocaleString()}</p>
                            </div>
                          </div>
                          
                          {offer.status === 'pending' && (
                            <Button
                              className="w-full mt-4 bg-accent hover:bg-gold-dark"
                              onClick={() => handleSelectOffer(offer.id)}
                              data-testid={`select-offer-${offer.id}`}
                            >
                              Select This Offer
                            </Button>
                          )}
                          
                          {offer.status === 'selected' && isInternal() && (
                            <Button
                              className="w-full mt-4 bg-green-600 hover:bg-green-700"
                              onClick={() => handleMarkFunded(offer.id, offer.approved_amount)}
                              data-testid={`fund-offer-${offer.id}`}
                            >
                              Mark as Funded
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* v3.14-173: Commissions tab REMOVED */}

        {/* Settings Tab (Admin Only) - FULL LENDER MANAGEMENT UI */}
        {isAdmin() && (
          <TabsContent value="settings" className="mt-6">
            <div className="space-y-6">
              {/* Lender Management */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Lender Management</CardTitle>
                    <CardDescription>
                      Configure lenders, API connections, and waterfall routing
                    </CardDescription>
                  </div>
                  <Button 
                    className="bg-accent hover:bg-gold-dark"
                    onClick={handleOpenCreateLender}
                    data-testid="add-lender-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add Lender
                  </Button>
                </CardHeader>
                <CardContent>
                  {lenders.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Landmark className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No lenders configured</p>
                      <p className="text-sm mt-1">Add a lender to start receiving offers</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Lender Name</TableHead>
                          <TableHead>API Type</TableHead>
                          <TableHead>Priority</TableHead>
                          <TableHead>Loan Range</TableHead>
                          <TableHead>Commission</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lenders.map((lender) => (
                          <TableRow key={lender.id} data-testid={`lender-row-${lender.id}`}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-slate-400" />
                                {lender.name}
                                {lender.has_api_key && (
                                  <KeyRound className="h-3 w-3 text-green-500" title="API Key configured" />
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {lender.api_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="font-mono text-sm">{lender.priority}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                ${lender.min_loan_amount?.toLocaleString()} - ${lender.max_loan_amount?.toLocaleString()}
                              </span>
                            </TableCell>
                            <TableCell>
                              {lender.commission_type ? (
                                <span className="text-sm">
                                  {lender.commission_type === 'percentage' 
                                    ? `${lender.commission_value}%` 
                                    : `$${lender.commission_value}`}
                                </span>
                              ) : (
                                <span className="text-slate-400 text-sm">Default (2%)</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Badge
                                  className={
                                    lender.enabled && lender.status === 'active'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-slate-100 text-slate-800'
                                  }
                                >
                                  {lender.enabled ? 'Enabled' : 'Disabled'}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleToggleLenderEnabled(lender)}
                                  title={lender.enabled ? 'Disable' : 'Enable'}
                                  data-testid={`toggle-lender-${lender.id}`}
                                >
                                  {lender.enabled ? (
                                    <PowerOff className="h-4 w-4 text-slate-500" />
                                  ) : (
                                    <Power className="h-4 w-4 text-green-500" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenEditLender(lender)}
                                  data-testid={`edit-lender-${lender.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-600"
                                  onClick={() => setDeleteLenderId(lender.id)}
                                  data-testid={`delete-lender-${lender.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Commission Rules REMOVED — v3.14-170 */}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Add/Edit Lender Dialog */}
      <Dialog open={isLenderDialogOpen} onOpenChange={setIsLenderDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLender ? 'Edit Lender' : 'Add New Lender'}</DialogTitle>
            <DialogDescription>
              Configure lender settings for the marketplace
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-slate-900">Basic Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="lender-name">Lender Name *</Label>
                  <Input
                    id="lender-name"
                    value={lenderForm.name}
                    onChange={(e) => setLenderForm({ ...lenderForm, name: e.target.value })}
                    placeholder="e.g., Upgrade, BestEgg"
                    data-testid="lender-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="api-type">API Type</Label>
                  <Select
                    value={lenderForm.api_type}
                    onValueChange={(v) => setLenderForm({ ...lenderForm, api_type: v })}
                  >
                    <SelectTrigger id="api-type" data-testid="lender-api-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mock">Mock (Testing)</SelectItem>
                      <SelectItem value="lendflow">Lendflow</SelectItem>
                      <SelectItem value="loanstack">LoanStack</SelectItem>
                      <SelectItem value="alloy">Alloy</SelectItem>
                      <SelectItem value="direct">Direct API</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="priority">Priority (lower = higher)</Label>
                  <Input
                    id="priority"
                    type="number"
                    value={lenderForm.priority}
                    onChange={(e) => setLenderForm({ ...lenderForm, priority: e.target.value })}
                    data-testid="lender-priority-input"
                  />
                </div>
              </div>
            </div>

            {/* API Configuration */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-slate-900">API Configuration</h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="api-endpoint">API Endpoint URL</Label>
                  <Input
                    id="api-endpoint"
                    value={lenderForm.api_endpoint}
                    onChange={(e) => setLenderForm({ ...lenderForm, api_endpoint: e.target.value })}
                    placeholder="https://api.lender.com/v1"
                    data-testid="lender-api-endpoint-input"
                  />
                </div>
                <div>
                  <Label htmlFor="api-key">
                    API Key 
                    {editingLender?.has_api_key && (
                      <span className="text-xs text-green-600 ml-2">(Key is configured)</span>
                    )}
                  </Label>
                  <Input
                    id="api-key"
                    type="password"
                    value={lenderForm.api_key}
                    onChange={(e) => setLenderForm({ ...lenderForm, api_key: e.target.value })}
                    placeholder={editingLender?.has_api_key ? 'Leave empty to keep existing key' : 'Enter API key'}
                    data-testid="lender-api-key-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    API key is stored securely and never displayed
                  </p>
                </div>
              </div>
            </div>

            {/* Loan Parameters */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-slate-900">Loan Parameters</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="min-loan">Minimum Loan Amount</Label>
                  <Input
                    id="min-loan"
                    type="number"
                    value={lenderForm.min_loan_amount}
                    onChange={(e) => setLenderForm({ ...lenderForm, min_loan_amount: e.target.value })}
                    data-testid="lender-min-loan-input"
                  />
                </div>
                <div>
                  <Label htmlFor="max-loan">Maximum Loan Amount</Label>
                  <Input
                    id="max-loan"
                    type="number"
                    value={lenderForm.max_loan_amount}
                    onChange={(e) => setLenderForm({ ...lenderForm, max_loan_amount: e.target.value })}
                    data-testid="lender-max-loan-input"
                  />
                </div>
                <div>
                  <Label htmlFor="min-apr">Minimum APR (%)</Label>
                  <Input
                    id="min-apr"
                    type="number"
                    step="0.01"
                    value={lenderForm.min_apr}
                    onChange={(e) => setLenderForm({ ...lenderForm, min_apr: e.target.value })}
                    data-testid="lender-min-apr-input"
                  />
                </div>
                <div>
                  <Label htmlFor="max-apr">Maximum APR (%)</Label>
                  <Input
                    id="max-apr"
                    type="number"
                    step="0.01"
                    value={lenderForm.max_apr}
                    onChange={(e) => setLenderForm({ ...lenderForm, max_apr: e.target.value })}
                    data-testid="lender-max-apr-input"
                  />
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-slate-900">Status & Assignment</h4>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label>Enabled</Label>
                  <p className="text-sm text-slate-500">Enable this lender in the marketplace</p>
                </div>
                <Switch
                  checked={lenderForm.enabled}
                  onCheckedChange={(checked) => setLenderForm({ ...lenderForm, enabled: checked })}
                  data-testid="lender-enabled-switch"
                />
              </div>
              <div>
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  value={lenderForm.description}
                  onChange={(e) => setLenderForm({ ...lenderForm, description: e.target.value })}
                  placeholder="Additional notes about this lender"
                  rows={2}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLenderDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveLender} 
              disabled={savingLender}
              className="bg-accent hover:bg-gold-dark"
              data-testid="save-lender-btn"
            >
              {savingLender && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingLender ? 'Update Lender' : 'Create Lender'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Commission Rule Dialog */}
      <Dialog open={isCommissionRuleDialogOpen} onOpenChange={setIsCommissionRuleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Commission Rule</DialogTitle>
            <DialogDescription>
              Configure commission calculation for a lender
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="rule-lender">Lender *</Label>
              <Select
                value={commissionRuleForm.lender_id}
                onValueChange={(v) => setCommissionRuleForm({ ...commissionRuleForm, lender_id: v })}
              >
                <SelectTrigger id="rule-lender" data-testid="commission-lender-select">
                  <SelectValue placeholder="Select a lender" />
                </SelectTrigger>
                <SelectContent>
                  {lenders.map((lender) => (
                    <SelectItem key={lender.id} value={lender.id}>
                      {lender.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="commission-type">Commission Type</Label>
              <Select
                value={commissionRuleForm.commission_type}
                onValueChange={(v) => setCommissionRuleForm({ ...commissionRuleForm, commission_type: v })}
              >
                <SelectTrigger id="commission-type" data-testid="commission-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="flat_fee">Flat Fee</SelectItem>
                  <SelectItem value="tiered">Tiered</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {commissionRuleForm.commission_type === 'percentage' && (
              <div>
                <Label htmlFor="percentage">Percentage (%)</Label>
                <Input
                  id="percentage"
                  type="number"
                  step="0.1"
                  value={commissionRuleForm.percentage}
                  onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, percentage: e.target.value })}
                  placeholder="e.g., 2.5"
                  data-testid="commission-percentage-input"
                />
              </div>
            )}

            {commissionRuleForm.commission_type === 'flat_fee' && (
              <div>
                <Label htmlFor="flat-fee">Flat Fee ($)</Label>
                <Input
                  id="flat-fee"
                  type="number"
                  value={commissionRuleForm.flat_fee}
                  onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, flat_fee: e.target.value })}
                  placeholder="e.g., 500"
                  data-testid="commission-flat-fee-input"
                />
              </div>
            )}

            {commissionRuleForm.commission_type === 'tiered' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tier-min">Tier Min Amount</Label>
                  <Input
                    id="tier-min"
                    type="number"
                    value={commissionRuleForm.tier_min || ''}
                    onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, tier_min: e.target.value })}
                    placeholder="Min loan amount"
                  />
                </div>
                <div>
                  <Label htmlFor="tier-max">Tier Max Amount</Label>
                  <Input
                    id="tier-max"
                    type="number"
                    value={commissionRuleForm.tier_max || ''}
                    onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, tier_max: e.target.value })}
                    placeholder="Max loan amount"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="tier-percentage">Rate for this Tier (%)</Label>
                  <Input
                    id="tier-percentage"
                    type="number"
                    step="0.1"
                    value={commissionRuleForm.percentage}
                    onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, percentage: e.target.value })}
                    placeholder="Commission % for this tier"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCommissionRuleDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCommissionRule} 
              disabled={savingCommissionRule}
              className="bg-accent hover:bg-gold-dark"
              data-testid="save-commission-rule-btn"
            >
              {savingCommissionRule && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Lender Confirmation */}
      <AlertDialog open={!!deleteLenderId} onOpenChange={() => setDeleteLenderId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lender?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the lender and all associated commission rules.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLender}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete Lender
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Application Confirmation */}
      <AlertDialog open={!!deleteApplicationId} onOpenChange={() => setDeleteApplicationId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this application? This action cannot be undone.
              <br /><br />
              This will also delete:
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>All associated lender offers</li>
                <li>All related commission records</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteApplication}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-application"
            >
              Delete Application
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* v3.14-167: Lender Offers Modal (Select ONE offer) */}
      <Dialog open={showOffersModal} onOpenChange={setShowOffersModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5 text-blue-600" />
              Available Financing Offers
              {selectedClient && (
                <span className="text-sm font-normal text-slate-500 ml-2">for {selectedClient.full_name}</span>
              )}
            </DialogTitle>
            <DialogDescription>
              Select ONE offer to save to the client record
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2" data-testid="lender-offers-list">
            {lenderOffers.map((offer) => {
              const isSelected = selectedOfferId === offer.offer_id;
              return (
                <div
                  key={offer.offer_id}
                  className={`border rounded-lg p-4 transition-colors cursor-pointer ${isSelected ? 'border-green-400 bg-green-50/50 ring-1 ring-green-200' : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50/30'}`}
                  onClick={() => setSelectedOfferId(offer.offer_id)}
                  data-testid={`lender-offer-${offer.offer_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-green-500 border-green-500' : 'border-slate-300'}`}>
                      {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800">{offer.lender_name}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase">Approved</p>
                          <p className="text-sm font-semibold text-green-600">${Number(offer.approved_amount).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase">APR</p>
                          <p className="text-sm font-semibold text-slate-700">{offer.apr}%</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase">Term</p>
                          <p className="text-sm font-semibold text-slate-700">{offer.term} months</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase">Monthly</p>
                          <p className="text-sm font-semibold text-blue-600">${Number(offer.monthly_payment).toLocaleString()}/mo</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowOffersModal(false); setSelectedOfferId(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveLenderOffer}
              disabled={!selectedOfferId || savingSelectedOffer}
              className="bg-accent hover:bg-gold-dark"
              data-testid="save-lender-offer-btn"
            >
              {savingSelectedOffer ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Save Selected Offer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LendingPage;
